import { Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { AutonomyModeSchema } from "./capabilities.js";
import { ContractVersion, IsoDateTimeSchema, OpaqueIdSchema, Sha256Schema, SubjectIdSchema, canonicalizeIsoDateTime } from "./primitives.js";
export const SourceBundleMediaType = "application/vnd.carbon.operations.source-bundle+json";
export const DraftMediaType = "text/markdown";
export const OperationsSummaryTemplateVersion = "operations-summary-v1";
export const ArtifactRefSchema = Type.String({
    minLength: 12,
    maxLength: 139,
    pattern: "^artifact://[A-Za-z0-9][A-Za-z0-9_-]*$",
});
export const SourceBundleSchema = Type.Object({
    schema: Type.Literal("carbon.operations.source-bundle/v1"),
    title: Type.String({ minLength: 1, maxLength: 200 }),
    summary_points: Type.Array(Type.String({ minLength: 1, maxLength: 2_000 }), { minItems: 1, maxItems: 100 }),
    evidence_refs: Type.Array(ArtifactRefSchema, { maxItems: 100, uniqueItems: true }),
}, { additionalProperties: false, $id: "carbon.operations.source-bundle/v1" });
export const ArtifactMetadataSchema = Type.Object({
    schema: Type.Literal("carbon.operations.artifact/v1"),
    artifact_id: OpaqueIdSchema,
    run_id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    kind: Type.Literal("document"),
    media_type: Type.Literal(DraftMediaType),
    byte_length: Type.Integer({ minimum: 0, maximum: 10 * 1024 * 1024 }),
    sha256: Sha256Schema,
    immutable: Type.Literal(true),
    created_at: IsoDateTimeSchema,
    content_href: Type.String({
        pattern: "^/api/carbon-operations/v1/artifacts/[A-Za-z0-9][A-Za-z0-9_-]*/content$",
    }),
}, { additionalProperties: false, $id: "carbon.operations.artifact/v1" });
// The validated ops.agent.action.v1 fields, published on approval.requested so approvers see what they decide.
export const AgentActionPayloadSchema = Type.Object({
    project: Type.String({ pattern: "^[a-z0-9][a-z0-9-]{1,62}$" }),
    action_class: Type.Union([
        Type.Literal("deploy"), Type.Literal("release"), Type.Literal("data-change"), Type.Literal("outbound-message"), Type.Literal("spend"),
        Type.Literal("credential-change"), Type.Literal("infra-change"), Type.Literal("code-merge"), Type.Literal("other"),
    ]),
    target: Type.String({ minLength: 1, maxLength: 256 }),
    summary: Type.String({ minLength: 1, maxLength: 2_000 }),
    plan_sha256: Sha256Schema,
    risk: Type.Union([Type.Literal("routine"), Type.Literal("high")]),
}, { additionalProperties: false });
const ApprovalInputSchema = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 128, pattern: "^[A-Za-z][A-Za-z0-9_-]*$" }),
    ref: ArtifactRefSchema,
    media_type: Type.String({ minLength: 3, maxLength: 128, pattern: "^[A-Za-z0-9.+-]+/[A-Za-z0-9.+-]+$" }),
    sha256: Sha256Schema,
}, { additionalProperties: false });
const ApprovalRequesterSchema = Type.Object({
    actor_id: SubjectIdSchema,
    on_behalf_of: Type.Object({
        subject_id: SubjectIdSchema,
        subject_type: Type.Union([Type.Literal("user"), Type.Literal("service")]),
    }, { additionalProperties: false }),
}, { additionalProperties: false });
const ApprovalExecutionSchema = Type.Union([
    Type.Object({
        kind: Type.Literal("document_draft"),
        template_version: Type.Literal(OperationsSummaryTemplateVersion),
        output_media_type: Type.Literal(DraftMediaType),
    }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal("authorization_receipt"),
        agent_action: AgentActionPayloadSchema,
        effect: Type.Literal("OperationSV authorizes this request and issues a receipt; it does not execute the action."),
    }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal("capability_request"),
    }, { additionalProperties: false }),
]);
/** The complete human-readable action envelope whose canonical JSON hashes to expected_action_sha256. */
export const ApprovalActionContextSchema = Type.Object({
    schema: Type.Literal("carbon.operations.approval-action-context/v1"),
    contract_version: Type.Literal(ContractVersion),
    capability_id: Type.String({ minLength: 1, maxLength: 160, pattern: "^[a-z0-9]+(?:[._-][a-z0-9]+)*$" }),
    capability_version: Type.Integer({ minimum: 1 }),
    tenant_id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    requester: ApprovalRequesterSchema,
    goal: Type.String({ minLength: 1, maxLength: 2_000 }),
    requested_autonomy: AutonomyModeSchema,
    inputs: Type.Array(ApprovalInputSchema, { maxItems: 100 }),
    event_sink_id: OpaqueIdSchema,
    run_expires_at: IsoDateTimeSchema,
    execution: ApprovalExecutionSchema,
}, { additionalProperties: false, $id: "carbon.operations.approval-action-context/v1" });
export const ApprovalRequestedPayloadSchema = Type.Object({
    approval_id: OpaqueIdSchema,
    expected_action_sha256: Sha256Schema,
    expires_at: IsoDateTimeSchema,
    reason: Type.String({ minLength: 1, maxLength: 1_000 }),
    action_context: ApprovalActionContextSchema,
    agent_action: Type.Optional(AgentActionPayloadSchema),
    // The requesting operator's internal operator_id (not its identity-provider subject); agent actions only.
    requested_by: Type.Optional(OpaqueIdSchema),
}, { additionalProperties: false });
export const ArtifactCreatedPayloadSchema = Type.Object({
    artifact_id: OpaqueIdSchema,
    artifact_ref: ArtifactRefSchema,
    kind: Type.Literal("document"),
    media_type: Type.Literal(DraftMediaType),
    byte_length: Type.Integer({ minimum: 0, maximum: 10 * 1024 * 1024 }),
    sha256: Sha256Schema,
    immutable: Type.Literal(true),
    template_version: Type.Literal(OperationsSummaryTemplateVersion),
    runner_id: OpaqueIdSchema,
    created_at: IsoDateTimeSchema,
    readback_verified_at: IsoDateTimeSchema,
}, { additionalProperties: false });
/** Validates and returns the one representation hashed by both server and browser. */
export function canonicalApprovalActionContext(value) {
    if (!Value.Check(ApprovalActionContextSchema, value)) {
        throw new TypeError("Approval action context does not match approval-action-context/v1");
    }
    return { ...value, run_expires_at: canonicalizeIsoDateTime(value.run_expires_at) };
}
export function isApprovalRequestedPayload(value) {
    return Value.Check(ApprovalRequestedPayloadSchema, value);
}
//# sourceMappingURL=artifacts.js.map